import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Briefcase, Check, Mail, MessageCircle, Phone, User, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type FormStep = {
  id: string;
  label: string;
  icon: React.ReactNode;
  type?: string;
  multiline?: boolean;
};

const formSteps: FormStep[] = [
  { id: "name", label: "What's your full name?", icon: <User /> },
  { id: "email", label: "Your work email", icon: <Mail />, type: "email" },
  { id: "org", label: "Organization you represent", icon: <Briefcase /> },
  { id: "phone", label: "Phone number (optional)", icon: <Phone />, type: "tel" },
  { id: "message", label: "Tell us about your program", icon: <Zap />, multiline: true },
];

export default function ContactFab() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="fixed right-4 bottom-4 z-40 md:right-6 md:bottom-6">
        <Button
          onClick={() => setOpen(true)}
          aria-label="Open contact"
          size="icon"
          className="size-12 rounded-full border border-border md:size-13"
        >
          <MessageCircle className="size-5!" />
        </Button>
      </div>
      <ContactModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function ContactModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [stepIdx, setStepIdx] = useState(0);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const reset = () => {
    setStepIdx(0);
    setValues({});
    setSubmitting(false);
    setSubmitted(false);
  };
  const handleClose = () => {
    onClose();
    setTimeout(reset, 300);
  };

  const current = formSteps[stepIdx];
  const isLast = stepIdx === formSteps.length - 1;
  const value = values[current?.id] || "";

  const next = () => {
    if (!value.trim()) return;
    if (isLast) {
      setSubmitting(true);
      setTimeout(() => {
        setSubmitting(false);
        setSubmitted(true);
      }, 900);
    } else {
      setStepIdx((s) => s + 1);
    }
  };
  const back = () => stepIdx > 0 && setStepIdx((s) => s - 1);

  const inputId = `contact-${current?.id}`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md gap-0 overflow-hidden bg-card p-0">
        <div className="px-5 pt-5 pb-4">
          <p className="eyebrow mb-2">Get in touch</p>
          <DialogTitle className="pr-8 text-display-sm">
            {submitted ? "Message received" : "Let's start a conversation"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            A short five-step form to contact the Taskpart team.
          </DialogDescription>

          {!submitted && (
            <div className="mt-4 flex gap-1" aria-hidden>
              {formSteps.map((s, i) => (
                <span
                  key={s.id}
                  className={cn(
                    "h-0.5 flex-1 rounded-full transition-colors duration-300",
                    i <= stepIdx ? "bg-foreground" : "bg-hairline",
                  )}
                />
              ))}
            </div>
          )}
        </div>

        <div className="px-5 pb-4">
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="py-4 text-center"
              >
                <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full border border-success/30 bg-success/10 text-success">
                  <Check className="size-6" />
                </div>
                <p className="text-title text-foreground">Thanks — we got it!</p>
                <p className="mx-auto mt-1.5 max-w-xs text-sm text-body">
                  Our operations team will reach out within one business day.
                </p>
              </motion.div>
            ) : (
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="pt-1"
              >
                <p className="eyebrow mb-1.5">
                  Step {stepIdx + 1} of {formSteps.length}
                </p>
                <Label htmlFor={inputId} className="mb-3 block text-body-md font-medium text-foreground">
                  {current.label}
                </Label>
                <div className="relative">
                  <span
                    className={cn(
                      "pointer-events-none absolute left-2.5 text-muted-foreground [&_svg]:size-4",
                      current.multiline ? "top-2.5" : "top-1/2 -translate-y-1/2",
                    )}
                    aria-hidden
                  >
                    {current.icon}
                  </span>
                  {current.multiline ? (
                    <Textarea
                      id={inputId}
                      rows={3}
                      autoFocus
                      value={value}
                      onChange={(e) => setValues((v) => ({ ...v, [current.id]: e.target.value }))}
                      placeholder={current.label}
                      className="pl-9"
                    />
                  ) : (
                    <Input
                      id={inputId}
                      type={current.type || "text"}
                      autoFocus
                      value={value}
                      onChange={(e) => setValues((v) => ({ ...v, [current.id]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          next();
                        }
                      }}
                      placeholder={current.label}
                      className="h-10 pl-9"
                    />
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {!submitted ? (
          <div className="flex items-center justify-between border-t border-border px-5 py-3">
            <Button variant="ghost" size="sm" onClick={back} disabled={stepIdx === 0}>
              Back
            </Button>
            <Button onClick={next} disabled={!value.trim()} loading={submitting} className="min-w-28">
              {submitting ? "Sending…" : isLast ? "Submit" : "Continue"}
              {!submitting && (isLast ? <Check /> : <ArrowRight />)}
            </Button>
          </div>
        ) : (
          <div className="px-5 pb-5">
            <Button onClick={handleClose} className="w-full">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
